import SEO from "@/components/SEO";
import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/pages/home/components/Footer";

interface ForumPost {
  id: string;
  user_id: string;
  content: string;
  media_url: string | null;
  media_type: string | null;
  file_name: string | null;
  created_at: string;
  profiles: {
    nome: string | null;
    avatar_url: string | null;
  } | null;
}

type MediaType = "image" | "pdf" | "audio" | "video" | "gif" | "link" | "file";

interface GifResult {
  id: string;
  url: string;
  preview: string;
  title: string;
}

const TENOR_KEY = "AIzaSyAyimkuYQYF_FXVALexPzkcsvZnUpdated3"; // Tenor GIF search key (placeholder)

export default function ForumPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const siteUrl = import.meta.env.VITE_SITE_URL || "https://example.com";
  const [posts, setPosts] = useState<ForumPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState("");
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [mediaPreview, setMediaPreview] = useState<{ url: string; file?: File; type: MediaType; gifUrl?: string; linkUrl?: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);
  const [showDocMenu, setShowDocMenu] = useState(false);
  const [showGifSearch, setShowGifSearch] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkInput, setLinkInput] = useState("");
  const [gifQuery, setGifQuery] = useState("");
  const [gifResults, setGifResults] = useState<GifResult[]>([]);
  const [gifLoading, setGifLoading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileAcceptRef = useRef<string>("*/*");
  const docMenuRef = useRef<HTMLDivElement>(null);

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Feed da Comunidade GodoyProd",
    url: `${siteUrl}/comunidade/forum`,
    description: "Feed e fórum da comunidade GodoyProd. Compartilhe partituras, músicas, ideias e conecte-se com outros músicos gospel.",
    inLanguage: "pt-BR",
  };

  useEffect(() => {
    loadPosts();
  }, []);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  // Fechar menu clicando fora
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (docMenuRef.current && !docMenuRef.current.contains(e.target as Node)) {
        setShowDocMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  async function loadPosts() {
    setLoading(true);
    const { data, error } = await supabase
      .from("community_posts")
      .select("*, profiles(nome, avatar_url)")
      .order("created_at", { ascending: false });

    if (error) {
      setToast({ type: "error", msg: "Erro ao carregar o feed." });
    } else {
      setPosts((data || []) as any);
    }
    setLoading(false);
  }

  function openFilePicker(accept: string) {
    fileAcceptRef.current = accept;
    setShowDocMenu(false);
    fileInputRef.current!.accept = accept;
    fileInputRef.current?.click();
  }

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 50 * 1024 * 1024) {
      setToast({ type: "error", msg: "Arquivo muito grande. Máximo 50MB." });
      return;
    }

    let type: MediaType = "file";
    if (file.type.startsWith("image/gif")) type = "gif";
    else if (file.type.startsWith("image/")) type = "image";
    else if (file.type === "application/pdf") type = "pdf";
    else if (file.type.startsWith("audio/")) type = "audio";
    else if (file.type.startsWith("video/")) type = "video";

    const url = URL.createObjectURL(file);
    setMediaPreview({ url, file, type });
    e.target.value = "";
  }

  async function searchGifs(query: string) {
    if (!query.trim()) return;
    setGifLoading(true);
    try {
      const res = await fetch(
        `https://tenor.googleapis.com/v2/search?q=${encodeURIComponent(query)}&key=AIzaSyAVQ9fv0K1qxhXW0LhLJovb-a6lNFtxXds&limit=12&media_filter=gif`
      );
      const data = await res.json();
      const results: GifResult[] = (data.results || []).map((r: any) => ({
        id: r.id,
        url: r.media_formats?.gif?.url || r.url,
        preview: r.media_formats?.tinygif?.url || r.media_formats?.gif?.url || r.url,
        title: r.content_description || r.title || "GIF",
      }));
      setGifResults(results);
    } catch {
      // fallback: busca via Giphy
      try {
        const res2 = await fetch(
          `https://api.giphy.com/v1/gifs/search?api_key=GlVGYHkr3WSBnllca54iNt0yFbjz7L65&q=${encodeURIComponent(query)}&limit=12&rating=g`
        );
        const data2 = await res2.json();
        const results: GifResult[] = (data2.data || []).map((r: any) => ({
          id: r.id,
          url: r.images?.original?.url || r.url,
          preview: r.images?.fixed_height_small?.url || r.images?.original?.url || r.url,
          title: r.title || "GIF",
        }));
        setGifResults(results);
      } catch {
        setGifResults([]);
      }
    }
    setGifLoading(false);
  }

  function selectGif(gif: GifResult) {
    setMediaPreview({ url: gif.url, type: "gif", gifUrl: gif.url });
    setShowGifSearch(false);
    setShowDocMenu(false);
    setGifResults([]);
    setGifQuery("");
  }

  function addLink() {
    if (!linkInput.trim()) return;
    let url = linkInput.trim();
    if (!url.startsWith("http")) url = "https://" + url;
    setMediaPreview({ url, type: "link", linkUrl: url });
    setLinkInput("");
    setShowLinkInput(false);
    setShowDocMenu(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!user) {
      navigate("/login?redirect=/comunidade/forum");
      return;
    }
    if (!newPost.trim() && !mediaPreview) {
      setToast({ type: "error", msg: "Escreva algo ou anexe um arquivo." });
      return;
    }

    setSubmitting(true);
    let mediaUrl: string | null = null;
    let mediaType: string | null = null;
    let fileName: string | null = null;

    try {
      if (mediaPreview) {
        if (mediaPreview.type === "link" && mediaPreview.linkUrl) {
          mediaUrl = mediaPreview.linkUrl;
          mediaType = "link";
        } else if (mediaPreview.type === "gif" && mediaPreview.gifUrl) {
          mediaUrl = mediaPreview.gifUrl;
          mediaType = "gif";
        } else if (mediaPreview.file) {
          setUploadingMedia(true);
          const file = mediaPreview.file;
          const path = `forum/${user.id}/${Date.now()}_${file.name.replace(/\s+/g, "_")}`;

          const { error: upError } = await supabase.storage
            .from("user-files")
            .upload(path, file, { upsert: true });

          if (upError) {
            // fallback REST upload usando URL do cliente supabase
            const session = await supabase.auth.getSession();
            const token = session.data.session?.access_token || "";
            const supabaseUrl = (supabase as any).supabaseUrl?.replace(/\/$/, "") || "";
            const restUrl = `${supabaseUrl}/storage/v1/object/user-files/${path}`;
            const restRes = await fetch(restUrl, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
                apikey: (supabase as any).supabaseKey || "",
                "x-upsert": "true",
                "Content-Type": file.type,
              },
              body: file,
            });
            if (!restRes.ok) throw new Error("Erro ao fazer upload do arquivo.");
            const publicUrl = `${supabaseUrl}/storage/v1/object/public/user-files/${path}`;
            mediaUrl = publicUrl;
          } else {
            const { data: urlData } = supabase.storage.from("user-files").getPublicUrl(path);
            mediaUrl = urlData.publicUrl;
          }
          mediaType = mediaPreview.type;
          fileName = file.name;
        }
      }

      const { error } = await supabase.from("community_posts").insert({
        user_id: user.id,
        content: newPost.trim(),
        media_url: mediaUrl,
        media_type: mediaType,
        file_name: fileName,
      });

      if (error) throw new Error(error.message);

      setNewPost("");
      setMediaPreview(null);
      setToast({ type: "success", msg: "Publicado com sucesso!" });
      loadPosts();
    } catch (err: any) {
      setToast({ type: "error", msg: err.message || "Erro ao publicar." });
    } finally {
      setSubmitting(false);
      setUploadingMedia(false);
    }
  }

  function formatDate(dateStr: string) {
    const d = new Date(dateStr);
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  }

  const docMenuItems = [
    { icon: "ri-image-line", label: "Foto / Imagem", accept: "image/*", type: "image" },
    { icon: "ri-video-line", label: "Vídeo", accept: "video/*", type: "video" },
    { icon: "ri-music-line", label: "Áudio", accept: "audio/*", type: "audio" },
    { icon: "ri-file-pdf-line", label: "PDF", accept: "application/pdf", type: "pdf" },
    { icon: "ri-file-line", label: "Arquivo", accept: "*/*", type: "file" },
  ];

  return (
    <>
      <SEO
        title="Feed da Comunidade"
        description="Feed e fórum da comunidade GodoyProd. Compartilhe partituras, músicas, ideias e conecte-se com outros músicos gospel."
        keywords="comunidade músicos gospel, feed músicos, partilhar partituras, fórum música gospel"
        schema={schema}
        canonicalPath="/comunidade/forum"
      />
      <div className="min-h-screen bg-godoy-black">
        <Navbar />

        <section className="pt-32 pb-20 md:pt-40 md:pb-28">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <Link to="/comunidade" className="inline-flex items-center gap-2 text-godoy-lightgray text-sm hover:text-godoy-gold transition-colors mb-4">
                <i className="ri-arrow-left-line" /> Voltar para Comunidade
              </Link>
              <h1 className="font-serif text-3xl md:text-4xl font-bold text-godoy-cream">
                Feed da Comunidade
              </h1>
              <p className="text-godoy-lightgray mt-2 text-sm">
                Compartilhe partituras, músicas, ideias e conecte-se com outros músicos.
              </p>
            </div>

            {/* Criar Post */}
            <div className="bg-godoy-charcoal/60 border border-godoy-gray/20 rounded-lg p-5 md:p-6 mb-10">
              {!user ? (
                <div className="text-center py-6">
                  <p className="text-godoy-lightgray text-sm mb-4">Faça login para participar do feed.</p>
                  <Link to="/login?redirect=/comunidade/forum" className="px-6 py-2.5 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all text-sm">
                    Entrar
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <textarea
                    value={newPost}
                    onChange={(e) => setNewPost(e.target.value)}
                    placeholder="Compartilhe uma ideia, prévia de música, partitura ou qualquer arquivo com a comunidade..."
                    rows={3}
                    maxLength={2000}
                    className="w-full px-4 py-3 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream placeholder:text-godoy-lightgray/40 focus:outline-none focus:border-godoy-gold/50 text-sm resize-none mb-3"
                  />

                  {/* Prévia da mídia */}
                  {mediaPreview && (
                    <div className="mb-3 relative">
                      {mediaPreview.type === "image" && (
                        <img src={mediaPreview.url} alt="prévia" className="max-h-48 rounded-md border border-godoy-gray/30 object-cover" />
                      )}
                      {mediaPreview.type === "gif" && (
                        <img src={mediaPreview.url} alt="GIF" className="max-h-48 rounded-md border border-godoy-gray/30" />
                      )}
                      {mediaPreview.type === "video" && (
                        <video src={mediaPreview.url} controls className="max-h-48 rounded-md border border-godoy-gray/30 w-full" />
                      )}
                      {mediaPreview.type === "audio" && (
                        <audio src={mediaPreview.url} controls className="w-full rounded-md" />
                      )}
                      {(mediaPreview.type === "pdf" || mediaPreview.type === "file") && (
                        <div className="flex items-center gap-2 px-4 py-3 bg-godoy-black/40 rounded-md border border-godoy-gray/30">
                          <i className={`${mediaPreview.type === "pdf" ? "ri-file-pdf-line text-red-400" : "ri-file-line text-godoy-gold"} text-xl`} />
                          <span className="text-godoy-cream text-sm truncate">{mediaPreview.file?.name}</span>
                        </div>
                      )}
                      {mediaPreview.type === "link" && (
                        <div className="flex items-center gap-2 px-4 py-3 bg-godoy-black/40 rounded-md border border-godoy-gray/30">
                          <i className="ri-links-line text-godoy-gold text-xl" />
                          <span className="text-godoy-gold text-sm truncate">{mediaPreview.linkUrl}</span>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setMediaPreview(null);
                          if (fileInputRef.current) fileInputRef.current.value = "";
                        }}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500/80 rounded-full flex items-center justify-center text-white text-xs hover:bg-red-500 transition-colors"
                      >
                        <i className="ri-close-line" />
                      </button>
                    </div>
                  )}

                  {/* Menu GIF */}
                  {showGifSearch && (
                    <div className="mb-3 p-3 bg-godoy-black/60 border border-godoy-gray/30 rounded-md">
                      <div className="flex gap-2 mb-3">
                        <input
                          type="text"
                          value={gifQuery}
                          onChange={(e) => setGifQuery(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); searchGifs(gifQuery); } }}
                          placeholder="Buscar GIFs... (ex: music, party, worship)"
                          className="flex-1 px-3 py-2 bg-godoy-charcoal border border-godoy-gray/30 rounded-md text-godoy-cream text-sm focus:outline-none focus:border-godoy-gold/50"
                        />
                        <button
                          type="button"
                          onClick={() => searchGifs(gifQuery)}
                          className="px-4 py-2 bg-godoy-gold text-godoy-black rounded-md text-sm font-medium hover:bg-godoy-goldlight transition-all whitespace-nowrap"
                        >
                          <i className="ri-search-line" />
                        </button>
                        <button
                          type="button"
                          onClick={() => { setShowGifSearch(false); setGifResults([]); setGifQuery(""); }}
                          className="px-2 py-2 text-godoy-lightgray hover:text-godoy-cream transition-colors"
                        >
                          <i className="ri-close-line" />
                        </button>
                      </div>
                      {gifLoading && (
                        <div className="flex items-center justify-center py-6">
                          <div className="w-6 h-6 border-2 border-godoy-gold border-t-transparent rounded-full animate-spin" />
                        </div>
                      )}
                      {gifResults.length > 0 && (
                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-52 overflow-y-auto">
                          {gifResults.map((gif) => (
                            <button
                              key={gif.id}
                              type="button"
                              onClick={() => selectGif(gif)}
                              className="rounded-md overflow-hidden hover:ring-2 hover:ring-godoy-gold transition-all aspect-square"
                            >
                              <img src={gif.preview} alt={gif.title} className="w-full h-full object-cover" />
                            </button>
                          ))}
                        </div>
                      )}
                      {gifResults.length === 0 && !gifLoading && (
                        <p className="text-godoy-lightgray/60 text-xs text-center py-2">
                          Busque por um termo para encontrar GIFs
                        </p>
                      )}
                    </div>
                  )}

                  {/* Campo de link */}
                  {showLinkInput && (
                    <div className="mb-3 flex gap-2">
                      <input
                        type="url"
                        value={linkInput}
                        onChange={(e) => setLinkInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLink(); } }}
                        placeholder="https://..."
                        className="flex-1 px-3 py-2 bg-godoy-black/60 border border-godoy-gray/30 rounded-md text-godoy-cream text-sm focus:outline-none focus:border-godoy-gold/50"
                      />
                      <button
                        type="button"
                        onClick={addLink}
                        className="px-4 py-2 bg-godoy-gold text-godoy-black rounded-md text-sm font-medium hover:bg-godoy-goldlight transition-all whitespace-nowrap"
                      >
                        Adicionar
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowLinkInput(false); setLinkInput(""); }}
                        className="px-2 py-2 text-godoy-lightgray hover:text-godoy-cream"
                      >
                        <i className="ri-close-line" />
                      </button>
                    </div>
                  )}

                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-1">
                      {/* Botão Documento com submenu */}
                      <div className="relative" ref={docMenuRef}>
                        <button
                          type="button"
                          onClick={() => { setShowDocMenu(!showDocMenu); setShowGifSearch(false); setShowLinkInput(false); }}
                          className="flex items-center gap-1.5 px-3 py-2 text-godoy-lightgray hover:text-godoy-gold hover:bg-godoy-gold/10 rounded-md text-sm transition-colors whitespace-nowrap"
                        >
                          <i className="ri-attachment-2" />
                          Documento
                          <i className={`ri-arrow-down-s-line text-xs transition-transform ${showDocMenu ? "rotate-180" : ""}`} />
                        </button>
                        {showDocMenu && (
                          <div className="absolute bottom-full left-0 mb-1 w-48 bg-godoy-charcoal border border-godoy-gray/30 rounded-lg shadow-xl z-50 overflow-hidden">
                            {docMenuItems.map((item) => (
                              <button
                                key={item.type}
                                type="button"
                                onClick={() => openFilePicker(item.accept)}
                                className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-godoy-cream hover:bg-godoy-gold/10 hover:text-godoy-gold transition-colors text-left"
                              >
                                <i className={`${item.icon} text-godoy-gold`} />
                                {item.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Botão GIF */}
                      <button
                        type="button"
                        onClick={() => { setShowGifSearch(!showGifSearch); setShowDocMenu(false); setShowLinkInput(false); }}
                        className="flex items-center gap-1.5 px-3 py-2 text-godoy-lightgray hover:text-godoy-gold hover:bg-godoy-gold/10 rounded-md text-sm transition-colors whitespace-nowrap font-bold"
                      >
                        GIF
                      </button>

                      {/* Botão Link */}
                      <button
                        type="button"
                        onClick={() => { setShowLinkInput(!showLinkInput); setShowDocMenu(false); setShowGifSearch(false); }}
                        className="flex items-center gap-1.5 px-3 py-2 text-godoy-lightgray hover:text-godoy-gold hover:bg-godoy-gold/10 rounded-md text-sm transition-colors whitespace-nowrap"
                      >
                        <i className="ri-links-line" />
                        Link
                      </button>

                      <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        onChange={handleFileSelect}
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-godoy-lightgray/50 text-xs">{newPost.length}/2000</span>
                      <button
                        type="submit"
                        disabled={submitting || uploadingMedia || (!newPost.trim() && !mediaPreview)}
                        className="px-6 py-2.5 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all text-sm disabled:opacity-50 whitespace-nowrap"
                      >
                        {submitting ? "Publicando..." : uploadingMedia ? "Enviando..." : "Publicar"}
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </div>

            {/* Feed */}
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-2 border-godoy-gold border-t-transparent rounded-full animate-spin" />
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-16 bg-godoy-charcoal/40 border border-godoy-gray/20 rounded-lg">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-godoy-gold/10 flex items-center justify-center">
                  <i className="ri-community-line text-2xl text-godoy-gold" />
                </div>
                <h3 className="font-serif text-xl text-godoy-cream mb-2">Nenhuma publicação ainda</h3>
                <p className="text-godoy-lightgray text-sm max-w-md mx-auto">
                  Seja o primeiro a compartilhar algo! Poste partituras, prévias de músicas ou ideias musicais.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {posts.map((post) => (
                  <div key={post.id} className="bg-godoy-charcoal/60 border border-godoy-gray/20 rounded-lg p-5 md:p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-godoy-charcoal border border-godoy-gold/20 flex-shrink-0">
                        {post.profiles?.avatar_url ? (
                          <img src={post.profiles.avatar_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <i className="ri-user-line text-godoy-lightgray" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <Link
                          to={`/comunidade/perfil/${post.user_id}`}
                          className="text-godoy-cream text-sm font-medium hover:text-godoy-gold transition-colors truncate block"
                        >
                          {post.profiles?.nome || "Músico da Comunidade"}
                        </Link>
                        <p className="text-godoy-lightgray/50 text-xs">{formatDate(post.created_at)}</p>
                      </div>
                    </div>

                    {post.content && (
                      <p className="text-godoy-lightgray text-sm leading-relaxed whitespace-pre-line mb-4">
                        {post.content}
                      </p>
                    )}

                    {/* Mídias */}
                    {post.media_url && (post.media_type === "image" || post.media_type === "gif") && (
                      <div className="mb-4 rounded-lg overflow-hidden border border-godoy-gray/20">
                        <img src={post.media_url} alt="mídia" className="w-full max-h-96 object-cover" />
                      </div>
                    )}
                    {post.media_url && post.media_type === "video" && (
                      <div className="mb-4 rounded-lg overflow-hidden border border-godoy-gray/20">
                        <video src={post.media_url} controls className="w-full max-h-96" />
                      </div>
                    )}
                    {post.media_url && post.media_type === "audio" && (
                      <div className="mb-4">
                        <audio src={post.media_url} controls className="w-full" />
                      </div>
                    )}
                    {post.media_url && (post.media_type === "pdf" || post.media_type === "file") && (
                      <a
                        href={post.media_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-4 bg-godoy-black/40 rounded-lg border border-godoy-gray/20 hover:border-godoy-gold/30 transition-colors mb-4"
                      >
                        <div className="w-10 h-10 rounded-md bg-red-500/10 flex items-center justify-center flex-shrink-0">
                          <i className={`${post.media_type === "pdf" ? "ri-file-pdf-line text-red-400" : "ri-file-line text-godoy-gold"} text-lg`} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-godoy-cream text-sm font-medium truncate">{post.file_name || "Documento"}</p>
                          <p className="text-godoy-gold text-xs hover:underline">Clique para visualizar</p>
                        </div>
                        <i className="ri-external-link-line text-godoy-lightgray ml-auto" />
                      </a>
                    )}
                    {post.media_url && post.media_type === "link" && (
                      <a
                        href={post.media_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-4 bg-godoy-black/40 rounded-lg border border-godoy-gray/20 hover:border-godoy-gold/30 transition-colors mb-4"
                      >
                        <div className="w-10 h-10 rounded-md bg-godoy-gold/10 flex items-center justify-center flex-shrink-0">
                          <i className="ri-links-line text-godoy-gold text-lg" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-godoy-gold text-sm truncate hover:underline">{post.media_url}</p>
                        </div>
                        <i className="ri-external-link-line text-godoy-lightgray ml-auto" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <Footer />

        {toast && (
          <div className="fixed bottom-6 right-6 z-[150] max-w-sm">
            <div className={`px-5 py-3 rounded-md text-sm font-medium border ${
              toast.type === "success"
                ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
                : "bg-red-500/10 border-red-500/30 text-red-400"
            }`}>
              <div className="flex items-center gap-2">
                <i className={toast.type === "success" ? "ri-check-line" : "ri-error-warning-line"} />
                {toast.msg}
                <button onClick={() => setToast(null)} className="ml-2 text-godoy-lightgray hover:text-godoy-cream">
                  <i className="ri-close-line" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}